import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { RotateCcw, Info } from 'react-feather';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import LoadingScreen from '../components/LoadingScreen';
import { InlineSelect, InlineCheckbox, BulkActionBar, StatusDot } from '../components/InlineTable';
import {
  ConfigPageShell, ConfigToolbar, FilterChip, ToolbarButton, ConfigEmptyState,
} from '../components/ConfigPages';
import {
  getOccupancyForecastMappings,
  saveOccupancyForecastMapping,
  saveOccupancyForecastMappingBulk,
  resetOccupancyForecastMapping,
} from '../utils/api';

/**
 * Forecast Sources — per-location routing of the two forecast slots:
 *
 *   On the Books (OTB)  -> what feeds Future OTB / Actual + OTB everywhere
 *   Projected           -> what feeds Projected Revenue/Rooms (the dashboard forecast)
 *
 * Each slot resolves most-specific-wins: a location row here overrides the
 * org/global defaults (typically the PMS feed). Options per location are the
 * normalized forecast types that actually have data for it (PMS mirror,
 * Lighthouse types, published forecasts, ...). Resetting a row reverts the
 * slot to the inherited default. Changes take effect at the next dashboard
 * fact refresh (and the next scrape/ETL run for downstream bridges).
 */

const SLOTS = ['OTB', 'Projected'];
const SLOT_LABELS = { OTB: 'On the Books source', Projected: 'Projected source' };
const SLOT_SHORT = { OTB: 'OTB', Projected: 'Projected' };

const SCOPE_TAG = {
  location: { cls: 'cp-tag cp-orange', label: 'custom', title: 'This location overrides the default — use the reset arrow to revert' },
  org: { cls: 'cp-tag cp-info', label: 'org default', title: 'Inherited from the organization default' },
  global: { cls: 'cp-tag', label: 'default', title: 'Inherited portfolio default (usually the PMS feed)' },
  none: { cls: 'cp-tag cp-warn', label: 'unmapped', title: 'No source resolves for this slot' },
};

const ScopeTag = ({ scope }) => {
  const t = SCOPE_TAG[scope] || SCOPE_TAG.none;
  return <span className={t.cls} title={t.title}>{t.label}</span>;
};

const slotOf = (loc, slot) => loc.slots?.[slot] || { typeValue: null, typeName: null, scope: 'none' };
const resolved = (loc, slot) => !!slotOf(loc, slot).typeValue;

export default function ForecastSourcesPage() {
  const { currentOrganization } = useUserSettingsContext();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [savingKey, setSavingKey] = useState(null); // `${locationId}|${slot}`
  const [search, setSearch] = useState('');
  const [customOnly, setCustomOnly] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkSlot, setBulkSlot] = useState('OTB');
  const [bulkApplying, setBulkApplying] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    try {
      const data = await getOccupancyForecastMappings(currentOrganization.id);
      setLocations(data.locations || []);
    } catch (error) {
      console.error('Failed to load forecast source mappings:', error);
      toast.error('Could not load forecast sources — reload the page to retry');
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [currentOrganization?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshNote = (res) => (res && res.refreshQueued === false
    ? ' — report data was NOT queued for refresh; run a dashboard refresh to see it'
    : ' — refreshing report data in the background');

  const handleChange = async (loc, slot, typeValue) => {
    if (!typeValue) return;
    const key = `${loc.locationId}|${slot}`;
    setSavingKey(key);
    try {
      const res = await saveOccupancyForecastMapping({ locationId: loc.locationId, slot, typeValue });
      toast.success(`${loc.name}: ${SLOT_SHORT[slot]} source saved${refreshNote(res)}`);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.error || `${loc.name}: could not save the ${SLOT_SHORT[slot]} source — try again`);
    } finally {
      setSavingKey(null);
    }
  };

  const handleReset = async (loc, slot) => {
    const key = `${loc.locationId}|${slot}`;
    setSavingKey(key);
    try {
      const res = await resetOccupancyForecastMapping(loc.locationId, slot);
      toast.success(`${loc.name}: ${SLOT_SHORT[slot]} reverted to default${refreshNote(res)}`);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.error || `${loc.name}: could not reset the ${SLOT_SHORT[slot]} source — try again`);
    } finally {
      setSavingKey(null);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────
  const total = locations.length;
  const customCounts = useMemo(() => {
    const c = { OTB: 0, Projected: 0 };
    locations.forEach((l) => SLOTS.forEach((s) => { if (slotOf(l, s).scope === 'location') c[s] += 1; }));
    return c;
  }, [locations]);
  const bothResolved = useMemo(() => locations.filter((l) => SLOTS.every((s) => resolved(l, s))).length, [locations]);
  const anyCustomCount = useMemo(() => locations.filter((l) => SLOTS.some((s) => slotOf(l, s).scope === 'location')).length, [locations]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter((l) => {
      if (q && !`${l.name || ''} ${l.code || ''}`.toLowerCase().includes(q)) return false;
      if (customOnly && !SLOTS.some((s) => slotOf(l, s).scope === 'location')) return false;
      return true;
    });
  }, [locations, search, customOnly]);

  const selectableVisible = useMemo(() => visible.filter((l) => (l.sources || []).length > 0), [visible]);
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((l) => selected.has(l.locationId));
  const someVisibleSelected = selectableVisible.some((l) => selected.has(l.locationId));

  // Bulk options = intersection of the selected locations' available sources.
  const bulkOptions = useMemo(() => {
    const picked = locations.filter((l) => selected.has(l.locationId));
    if (!picked.length) return [];
    let common = null;
    picked.forEach((l) => {
      const set = new Map((l.sources || []).map((s) => [s.value, s.label]));
      if (common === null) common = set;
      else common = new Map([...common].filter(([v]) => set.has(v)));
    });
    return [...(common || new Map())].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [locations, selected]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allVisibleSelected) selectableVisible.forEach((l) => n.delete(l.locationId));
    else selectableVisible.forEach((l) => n.add(l.locationId));
    return n;
  });

  const handleBulkApply = async (typeValue) => {
    if (!typeValue) return;
    const locationIds = [...selected];
    setBulkApplying(true);
    try {
      const res = await saveOccupancyForecastMappingBulk({ locationIds, slot: bulkSlot, typeValue });
      const n = typeof res?.updated === 'number' ? res.updated : locationIds.length;
      toast.success(`${SLOT_SHORT[bulkSlot]} source set on ${n} location${n === 1 ? '' : 's'}${refreshNote(res)}`);
      setSelected(new Set());
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.error || `Could not set the ${SLOT_SHORT[bulkSlot]} source on the selected locations — nothing was changed`);
    } finally {
      setBulkApplying(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (loading && !loadedOnce) return <LoadingScreen />;

  const stations = [
    { label: 'Locations', value: total.toLocaleString() },
    { label: 'OTB source', value: `${customCounts.OTB} custom`, sub: `${total - customCounts.OTB} inherit the default`, tone: customCounts.OTB > 0 ? 'ok' : 'neutral' },
    { label: 'Projected source', value: `${customCounts.Projected} custom`, sub: `${total - customCounts.Projected} inherit the default`, tone: customCounts.Projected > 0 ? 'ok' : 'neutral' },
    { label: 'Lands in', value: 'Dashboards & reports', mono: false, sub: 'Future OTB · Projected revenue & rooms' },
  ];

  const emptyState = total === 0
    ? { title: 'No locations in this organization', body: 'Forecast sources are chosen per location. Add a location first.' }
    : {
      title: customOnly && !search.trim() ? 'No locations with a custom source' : `No locations match${search.trim() ? ` “${search.trim()}”` : ''}${customOnly ? ' with a custom source' : ''}`,
      body: customOnly && !search.trim() ? 'Every location inherits its OTB and Projected sources from the default.' : 'Clear the search or the Custom only chip to see more rows.',
      action: <ToolbarButton variant="ghost" onClick={() => { setSearch(''); setCustomOnly(false); }}>Clear filters</ToolbarButton>,
    };

  return (
    <ConfigPageShell
      title="Forecast sources"
      subtitle="Which forecast feeds each location's On the Books and Projected numbers across dashboards and reports."
      flow={{
        stations,
        coverage: total ? { mapped: bothResolved, total, label: 'locations with both sources resolved' } : undefined,
      }}
    >
      <div className="cp-note" style={{ marginBottom: 12 }}>
        <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Every source blends actuals to date with that forecast&apos;s future days.
          Saving a change rebuilds the location&apos;s report data in the background — allow a few minutes.
          <strong> default</strong> = inherited portfolio default (usually the PMS feed);
          <strong> custom</strong> = this location overrides it (use the reset arrow to revert).
        </span>
      </div>

      <div className="it-frame" aria-busy={loading}>
        <ConfigToolbar
          search={{ value: search, onChange: setSearch, placeholder: 'Search location name or code…' }}
          chips={(
            <FilterChip active={customOnly} count={anyCustomCount} onClick={() => setCustomOnly((v) => !v)} title="Only locations that override a default">
              Custom only
            </FilterChip>
          )}
          meta={`${visible.length.toLocaleString()} location${visible.length === 1 ? '' : 's'}`}
        />

        {selected.size > 0 && (
          <BulkActionBar
            key={bulkSlot}
            count={selected.size}
            noun="selected"
            onClear={() => setSelected(new Set())}
            actionLabel={`Set ${SLOT_SHORT[bulkSlot]} source`}
            options={bulkOptions}
            placeholder={bulkOptions.length ? 'Choose source…' : 'No source shared by all selected'}
            applyLabel="Apply"
            applying={bulkApplying}
            onApply={handleBulkApply}
          >
            <div style={{ display: 'inline-flex', gap: 4 }} role="group" aria-label="Bulk slot">
              {SLOTS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setBulkSlot(s)}
                  aria-pressed={bulkSlot === s}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'var(--font-sans)', border: '1px solid rgba(255,255,255,.14)',
                    background: bulkSlot === s ? 'rgba(255,255,255,.14)' : 'transparent',
                    color: bulkSlot === s ? 'var(--lodge-onyx-50)' : 'rgba(255,255,255,.55)',
                  }}
                >
                  {SLOT_SHORT[s]}
                </button>
              ))}
            </div>
          </BulkActionBar>
        )}

        {visible.length === 0 ? (
          <ConfigEmptyState {...emptyState} />
        ) : (
          <div className="it-scroll">
            <table className="it-tbl" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: 'center', padding: 0 }}>
                    <InlineCheckbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={selectableVisible.length === 0}
                      title="Select all visible"
                    />
                  </th>
                  <th>Location</th>
                  {SLOTS.map((slot) => <th key={slot} style={{ minWidth: 320 }}>{SLOT_LABELS[slot]}</th>)}
                  <th style={{ width: 64, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((loc) => {
                  const hasSources = (loc.sources || []).length > 0;
                  const isSel = selected.has(loc.locationId);
                  const both = SLOTS.every((s) => resolved(loc, s));
                  const any = SLOTS.some((s) => resolved(loc, s));
                  const status = both
                    ? { state: 'complete', title: `OTB: ${slotOf(loc, 'OTB').typeName} · Projected: ${slotOf(loc, 'Projected').typeName}` }
                    : any
                      ? { state: 'partial', title: `${SLOTS.filter((s) => !resolved(loc, s)).map((s) => SLOT_SHORT[s]).join(' and ')} unresolved — that slot shows no forecast` }
                      : { state: 'none', title: 'No forecast source resolves for this location yet' };
                  return (
                    <tr key={loc.locationId} className={`cp-row${isSel ? ' it-row-selected' : ''}`}>
                      <td style={{ textAlign: 'center' }}>
                        <InlineCheckbox checked={isSel} onChange={() => toggleSelect(loc.locationId)} disabled={!hasSources} title={hasSources ? 'Select row' : 'No forecast data yet'} />
                      </td>
                      <td>
                        <div className="cp-cell">
                          <div className="cp-name">{loc.name}</div>
                          {loc.code ? <div className="cp-code">{loc.code}</div> : null}
                        </div>
                      </td>
                      {SLOTS.map((slot) => {
                        const slotInfo = slotOf(loc, slot);
                        const key = `${loc.locationId}|${slot}`;
                        return (
                          <td key={slot}>
                            {hasSources ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <InlineSelect
                                    value={slotInfo.typeValue}
                                    options={loc.sources.map((s) => ({ value: s.value, label: s.label }))}
                                    onChange={(value) => handleChange(loc, slot, value)}
                                    placeholder={slotInfo.typeName || 'Select source…'}
                                    rowActive
                                    clearable={false}
                                    disabled={savingKey === key}
                                    minWidth={220}
                                    ariaLabel={`${SLOT_LABELS[slot]} for ${loc.name}`}
                                  />
                                </div>
                                <ScopeTag scope={slotInfo.scope} />
                                {slotInfo.scope === 'location' ? (
                                  <ToolbarButton
                                    small iconOnly variant="ghost" icon={RotateCcw}
                                    onClick={() => handleReset(loc, slot)}
                                    disabled={savingKey === key}
                                    title="Revert to inherited default"
                                    aria-label={`Revert ${SLOT_SHORT[slot]} source for ${loc.name}`}
                                  />
                                ) : <span style={{ width: 28, flexShrink: 0 }} aria-hidden="true" />}
                              </div>
                            ) : (
                              <div className="cp-lands" style={{ whiteSpace: 'normal' }}>
                                No forecast data for this location yet
                                {slotInfo.typeName ? <> · inherits <strong>{slotInfo.typeName}</strong></> : ''}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td><StatusDot state={status.state} title={status.title} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ConfigPageShell>
  );
}
